'use client';

import { useState } from 'react';
import Link from 'next/link';
import { paymentBreakdown } from '@/lib/mortgage';

interface MonthlyPaymentProps {
  price: number;
  hoaFee?: number;
}

const RATE_OPTIONS = [5.5, 6.0, 6.5, 7.0, 7.5];
const DOWN_OPTIONS = [10, 20, 30, 40];
const TERM_OPTIONS = [15, 30];

export default function MonthlyPayment({ price, hoaFee = 0 }: MonthlyPaymentProps) {
  const [rate, setRate] = useState(6.5);
  const [down, setDown] = useState(20);
  const [years, setYears] = useState(30);

  const b = paymentBreakdown(price, down, rate, years, hoaFee);

  const selectClass =
    'rounded-lg border border-hairline bg-canvas px-2 py-1 text-sm text-ink outline-none focus:border-emerald-500/50';

  return (
    <div className="glass rounded-card p-6">
      <h3 className="font-display mb-4 text-lg text-ink">Estimated monthly payment</h3>
      <p className="font-display text-4xl tabular-nums text-gold-400">
        ${b.total.toLocaleString()}
        <span className="font-sans text-base text-ink-muted">/mo</span>
      </p>

      {/* Assumptions — editable */}
      <div className="mt-4 flex flex-wrap gap-2">
        <label className="flex items-center gap-1.5 text-sm text-ink-muted">
          Rate
          <select className={selectClass} value={rate} onChange={(e) => setRate(Number(e.target.value))}>
            {RATE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}%
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-sm text-ink-muted">
          Down
          <select className={selectClass} value={down} onChange={(e) => setDown(Number(e.target.value))}>
            {DOWN_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}%
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-sm text-ink-muted">
          Term
          <select className={selectClass} value={years} onChange={(e) => setYears(Number(e.target.value))}>
            {TERM_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y} yr
              </option>
            ))}
          </select>
        </label>
      </div>

      <dl className="mt-4 space-y-1.5 text-sm">
        <div className="flex justify-between">
          <dt className="text-ink-muted">Principal &amp; interest</dt>
          <dd className="tabular-nums text-ink-soft">${Math.round(b.pi).toLocaleString()}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-muted">Property tax (est.)</dt>
          <dd className="tabular-nums text-ink-soft">${Math.round(b.tax).toLocaleString()}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-muted">Insurance (est.)</dt>
          <dd className="tabular-nums text-ink-soft">${Math.round(b.insurance).toLocaleString()}</dd>
        </div>
        {hoaFee > 0 && (
          <div className="flex justify-between">
            <dt className="text-ink-muted">HOA</dt>
            <dd className="tabular-nums text-ink-soft">${hoaFee.toLocaleString()}</dd>
          </div>
        )}
      </dl>
      <p className="mt-3 text-xs text-ink-faint">
        {down}% down, {rate}% APR, {years}-year fixed, incl. est. tax &amp; insurance. Estimate
        only.
      </p>
      <div className="mt-4 space-y-2 border-t border-hairline pt-4">
        <Link
          href="/learn/mortgage-calculator"
          className="block text-sm font-medium text-emerald-300 transition-colors hover:text-emerald-700"
        >
          Use the full mortgage calculator →
        </Link>
        <Link
          href="/pricing"
          className="block text-sm font-medium text-emerald-300 transition-colors hover:text-emerald-700"
        >
          How the $5,000 flat fee works →
        </Link>
      </div>
    </div>
  );
}
